/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import _ from 'underscore';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import mapProps from 'js/lib/mapProps';
import path from 'js/lib/path';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import provideFluxibleAppContext from 'js/lib/provideFluxibleAppContext';

import ensureEnrolled from 'pages/open-course/common/utils/ensureEnrolled';

import { Button } from '@coursera/cds-core';

import { CMLUtils } from 'bundles/cml';
import { addThread } from 'bundles/discussions/actions/ThreadsActions';
import { ContentInput, ForumSelector, TitleInput } from 'bundles/discussions/components/NewThreadForm';
import RequiredField from 'bundles/discussions/components/RequiredField';
import discussionsForumsHOC from 'bundles/discussions/components/discussionsForumsHOC';
import SessionFilterDropdown from 'bundles/discussions/components/personalization-sessionFilter/SessionFilterDropdown';
import { DTD_NAME, naptimeForumTypes, savingStates } from 'bundles/discussions/constants';
import DiscussionsCMLUtils from 'bundles/discussions/utils/DiscussionsCMLUtils';
import { buildUrl } from 'bundles/discussions/utils/discussionsUrl';
import routerConnectToCurrentForum from 'bundles/discussions/utils/routerConnectToCurrentForum';
import Icon from 'bundles/iconfont/Icon';
import TrackedButton from 'bundles/page/components/TrackedButton';
import Modal from 'bundles/ui/components/Modal';

import _t from 'i18n!nls/discussions';

import 'css!bundles/discussions/components/__styles__/NewThreadButton';

type Props = {
  rootForumId?: string;
  forums: Array<Record<string, any>>;
  showSessionSelector?: boolean;
  courseId?: string;
  currentForum?: any;
  verificationDisplay?: any;
  course?: any;
  userId?: number;
  filter?: any;
  rootPath?: string;
  threadSavingState?: string;
  newThread: string;
};

type State = {
  showModal: boolean;
  title: any;
  content: any;
  currentForum: any;
  showErrors: boolean;
  saving: boolean;
  saveError: boolean;
  saveErrorMessage: string;
  activeFilterId: string;
};

type Context = {
  router: any;
};

class NewThreadButton extends React.Component<Props, State> {
  declare newThreadButton: TrackedButton | null;

  declare submitButton: TrackedButton | null;

  declare contentElemRef: HTMLElement | null;

  declare forumElemRef: HTMLElement | null;

  declare titleElemRef: HTMLElement | null;

  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof NewThreadButton.contextTypes>;

  constructor(props: Props, context: Context) {
    super(props, context);

    this.state = this.resetState();

    // @ts-expect-error TSMIGRATION
    this.titleElemRef = {};
    // @ts-expect-error TSMIGRATION
    this.contentElemRef = {};
    // @ts-expect-error TSMIGRATION
    this.forumElemRef = {};
  }

  componentWillReceiveProps(nextProps: any) {
    const { threadSavingState } = this.props;
    const { saving } = this.state;
    // don't update the state while saving, otherwise the current forum
    // may be null when trying to navigate to the location of the new post
    if (!saving) {
      const { currentForum } = this.props;
      const currentForumValue = currentForum;
      const currentForumError = !currentForumValue;
      this.setState({
        currentForum: {
          value: currentForumValue,
          hasError: currentForumError,
        },
      });
    }

    if (threadSavingState === savingStates.SAVING && nextProps.threadSavingState === savingStates.SAVED) {
      this.handleSaveSuccess(nextProps.newThread);
    } else if (threadSavingState === savingStates.SAVING && nextProps.threadSavingState === savingStates.ERROR) {
      this.handleSaveError();
    }
  }

  handleSaveSuccess(newThread: any) {
    const { router } = this.context;
    const { currentForum } = this.state;
    const { rootPath } = this.props;
    // if we are in the aggregate view, navigate to the appropriate forum
    // @ts-expect-error TSMIGRATION
    const forumPath = path.join(rootPath, currentForum.value.link);
    router.push(buildUrl(forumPath, newThread.questionId));
    this.setState(this.resetState());
  }

  handleSaveError() {
    this.setState({
      saveError: true,
      saveErrorMessage: _t('Something went wrong. Please reload the page and try again.'),
      saving: false,
    });
  }

  handleSubmit = () => {
    const { currentForum, activeFilterId, title, content } = this.state;
    const { userId, filter, showSessionSelector } = this.props;
    const { executeAction } = this.context;
    const hasAnyErrors = _(this.state).any((item) => item.hasError);

    if (hasAnyErrors) {
      if (title.hasError) {
        // @ts-expect-error TSMIGRATION
        this.titleElemRef.focus();
      } else if (content.hasError) {
        // @ts-expect-error TSMIGRATION
        this.contentElemRef.focus();
      } else if (currentForum.hasError) {
        // @ts-expect-error TSMIGRATION
        this.forumElemRef.focus();
      }
      this.setState({ showErrors: true });
    } else {
      const threadPostData = {
        title: title.value,
        content: content.value,
        context: '',
      };

      if (showSessionSelector) {
        threadPostData.context = activeFilterId;
      } else if (filter) {
        threadPostData.context = filter.id;
      }

      const showOverrideSessionId =
        !!threadPostData.context && threadPostData.context !== 'all' && threadPostData.context !== 'ondemand';
      const threadData = {
        content: {
          question: threadPostData.title,
          details: threadPostData.content,
        },
        overrideSessionId: showOverrideSessionId ? threadPostData.context : undefined,
        state: {},
        userId,
        courseForumId: currentForum.value.id,
        mentorForumId: currentForum.value.id,
        groupForumId: currentForum.value.id,
      };
      const { forumId } = currentForum.value;
      const forumType = currentForum.value.forumType && currentForum.value.forumType.typeName;
      this.setState({ saving: true });

      executeAction(addThread, {
        options: threadData,
        forumType,
        forumId,
      });
    }
  };

  handleDropdownChange = (filterId: string) => {
    this.setState({
      activeFilterId: filterId,
    });
  };

  handleOpenModal = () => {
    const { userId, course, verificationDisplay } = this.props;
    ensureEnrolled(userId, course, verificationDisplay).then(() => this.handleOpen());
  };

  handleCancel = () => {
    this.setState(this.resetState());
  };

  handleCloseModal = () => {
    this.setState({
      showModal: false,
    });
  };

  handleInputChange = (inputName: string, event: any) => {
    // @ts-expect-error TSMIGRATION
    const { [inputName]: field } = this.state;

    field.value = event.value;
    field.hasError = !event.isValid;
    this.setState((prevState) => prevState);
  };

  handleOpen() {
    const { showModal } = this.state;
    this.setState(() => ({ showModal: !showModal }));
  }

  // remove and reinject error messages to retrigger screenreader alerts for a11y
  clearErrorsAndSubmit = () => {
    this.setState({ showErrors: false });
    setTimeout(() => {
      this.handleSubmit();
    }, 500);
  };

  resetState() {
    const { currentForum } = this.props;
    const currentForumValue = currentForum;
    const currentForumError = !currentForumValue;
    return {
      showModal: false,
      title: {
        value: '',
        hasError: true,
      },
      content: {
        value: CMLUtils.create('', DTD_NAME),
        hasError: true,
      },
      currentForum: {
        value: currentForumValue,
        hasError: currentForumError,
      },
      showErrors: false,
      saving: false,
      saveError: false,
      saveErrorMessage: '',
      activeFilterId: '',
    };
  }

  setTitleElemRef = (elem: any) => {
    this.titleElemRef = elem;
  };

  setContentElemRef = (elem: any) => {
    this.contentElemRef = elem;
  };

  setForumElemRef = (elem: any) => {
    this.forumElemRef = elem;
  };

  render() {
    const { showModal, saving, saveError, content, showErrors, title, currentForum, saveErrorMessage } = this.state;
    const { courseId, showSessionSelector, rootForumId, forums } = this.props;
    const contentLengthWarning = DiscussionsCMLUtils.generateContentLengthWarning(content.value);

    if (!forums || !forums.length) return null;

    const saveErrorDisplay = <div className="error-text c-modal-error-message">{saveErrorMessage}</div>;

    let forumSelector: React.ReactNode | null = null;
    const currentForumValue = currentForum.value;
    const forumsWithoutSubforums = forums.filter(
      (forum) => !forum.parentForumId || forum.parentForumId === rootForumId
    );
    // reorder the forums so that subforums are directly under parent forums
    const forumsWithNestedSubforums: Array<any> = _(
      forumsWithoutSubforums.map((parentForum) =>
        [parentForum].concat(forums.filter((forum) => forum.parentForumId === parentForum.id))
      )
    ).flatten();

    if (forumsWithNestedSubforums.length) {
      forumSelector = (
        <div className="c-input-area">
          <RequiredField fieldName={_t('Forum')} />
          <ForumSelector
            id="forum-selector"
            rootForumId={rootForumId}
            hasError={showErrors && currentForum.hasError}
            handleInputChange={this.handleInputChange}
            forums={forumsWithNestedSubforums}
            value={currentForumValue}
            elemRefHook={this.setForumElemRef}
          />
        </div>
      );
    }

    let sessionSelector: undefined | React.ReactNode;
    if (showSessionSelector) {
      sessionSelector = (
        <div className="context-selector-container">
          <div
            css={css`
              float: left;
            `}
            className="bgcolor-divider section-divider"
          />
          <div className="c-input-area">
            <h3 className="headline-1-text">{_t("You're currently viewing All Sessions.")}</h3>
            <label htmlFor="new-thread-sessions-switcher">
              <span className="caption-text color-secondary-text">{_t('Which session should this thread go in?')}</span>
            </label>
            <SessionFilterDropdown
              id="new-thread-sessions-switcher"
              hideAllSessionsOption
              limitSwitchingToCurrentBranch
              dropup
              handleDropdownChange={this.handleDropdownChange}
            />
          </div>
        </div>
      );
    }

    const modal = (
      <Modal
        onRequestClose={this.handleCloseModal}
        modalName={_t('Create a new thread')}
        className="rc-NewThreadModal large"
      >
        <h1 className="c-modal-title headline-3-text">{_t('Create a new thread')}</h1>
        <div className="c-input-area">
          <RequiredField fieldName={_t('Title')} />
          <TitleInput
            id="title-input"
            hasError={showErrors && title.hasError}
            value={title.value}
            handleInputChange={this.handleInputChange}
            elemRefHook={this.setTitleElemRef}
          />
        </div>
        <div className="c-input-area">
          <RequiredField fieldName={_t('Body')} />
          <ContentInput
            id="content-input"
            courseId={courseId}
            hasError={showErrors && content.hasError}
            handleInputChange={this.handleInputChange}
            cml={content.value}
            elemRefHook={this.setContentElemRef}
          />
        </div>
        {forumSelector}
        {sessionSelector}
        <div>
          {contentLengthWarning}
          {saveError && saveErrorDisplay}
          <div className="horizontal-box align-items-right">
            <button type="button" className="passive" onClick={this.handleCancel}>
              {_t('Cancel')}
            </button>
            &nbsp;&nbsp;
            <TrackedButton
              ref={(node) => {
                this.submitButton = node;
              }}
              disabled={saving}
              className="secondary"
              type="submit"
              onClick={this.clearErrorsAndSubmit}
              trackingName="new_thread_create"
            >
              {_t('Post')}
            </TrackedButton>
          </div>
        </div>
      </Modal>
    );

    return (
      <div className="rc-NewThreadForm shrink-block horizontal-box align-items-right">
        <Button
          className="large-screen"
          variant="secondary"
          size="small"
          component={TrackedButton}
          trackingName="new_thread_button"
          withVisibilityTracking={true}
          requireFullyVisible={false}
          onClick={this.handleOpenModal}
          aria-label={_t('New Thread')}
          ref={(node: TrackedButton) => {
            this.newThreadButton = node;
          }}
        >
          {_t('New Thread')}
        </Button>
        <Button
          className="nostyle align-right mobile hide"
          variant="ghost"
          size="small"
          component={TrackedButton}
          trackingName="new_thread_button"
          withVisibilityTracking={true}
          requireFullyVisible={false}
          onClick={this.handleOpenModal}
          aria-label={_t('New Thread')}
          ref={(node: TrackedButton) => {
            this.newThreadButton = node;
          }}
        >
          <Icon name="plus" />
        </Button>
        {showModal && modal}
      </div>
    );
  }
}

const StoreConnectedNewThreadButton = _.compose(
  // TODO: connectToStores<Props, InputProps, Stores>
  connectToStores<any, any>(
    [
      'SessionFilterStore',
      'ThreadsStore',
      'ApplicationStore',
      'CourseMembershipStore',
      'VerificationStore',
      'CourseStore',
    ],
    (
      { SessionFilterStore, ThreadsStore, ApplicationStore, CourseMembershipStore, VerificationStore, CourseStore },
      props
    ) => ({
      showSessionSelector:
        (ApplicationStore.isSuperuser() || CourseMembershipStore.hasModerationRole()) &&
        SessionFilterStore.activeFilterQueryString === 'all',
      filter: SessionFilterStore.activeFilter,
      threadSavingState: ThreadsStore.savingState,
      newThread: ThreadsStore.threadToInsert,
      userId: ApplicationStore.getUserData().id,
      verificationDisplay: VerificationStore.getVerificationDisplay(),
      course: CourseStore.getMetadata(),
      courseId: CourseStore.getCourseId(),
    })
  ),
  discussionsForumsHOC({ fields: ['forumType', 'title', 'link'] }),
  connectToRouter(routerConnectToCurrentForum),
  mapProps<any, any>((props) => {
    const currentForum =
      (props.currentForum &&
        props.currentForum.forumType.typeName !== naptimeForumTypes.rootForumType &&
        props.currentForum) ||
      undefined;
    const rootForum =
      props.courseForums &&
      props.courseForums.find((forum: $TSFixMe) => forum.forumType.typeName === naptimeForumTypes.rootForumType);
    return {
      forums: props.courseForums
        ? props.courseForums
            .filter((forum: $TSFixMe) => forum.forumType.typeName !== naptimeForumTypes.rootForumType)
            .concat(props.mentorForums)
            .concat(props.groupForums)
        : [],
      rootForumId: rootForum && rootForum.id,
      // don't allow users to post to the root forum, so unset the current forum if it is root.
      currentForum,
      currentForumId: currentForum && currentForum.id,
    };
  })
)(NewThreadButton);

export default provideFluxibleAppContext()(StoreConnectedNewThreadButton);

// So that we can require this as a naptime component
export const NewThreadButtonNaptimeContainer = StoreConnectedNewThreadButton;

export const BaseComp = NewThreadButton;
